import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../utils/supabaseClient";

import {
  PageWrap,
  Header,
  HeaderLeft,
  HeaderRight,
  HeaderCenter,
  Title,
  Subtitle,
  Ornament,
  OrnLine,
  OrnDiamond,
  Badge,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  Layout,
  Sidebar,
  SearchInput,
  FilterRow,
  Select,
  ProductList,
  ProductCard,
  ProductTop,
  ProductName,
  ProductMeta,
  ProductStatus,
  ProductPrice,
  ProductActions,
  Main,
  Panel,
  PanelTitle,
  FormGrid,
  Field,
  Label,
  Input,
  TextArea,
  CheckboxRow,
  CheckboxLabel,
  InlineRow,
  ImagePreview,
  EmptyState,
  InfoBox,
  Divider,
  SmallText,
  ForbiddenWrap,
  Section,
  SectionTitle,
  SectionHint,
  ToggleGrid,
  HelperText,
  StatusBox,
  HiddenFileInput,
  UploadButton,
  MessageBox,
  MessageIcon,
  MessageContent,
  MessageTitle,
  MessageText,
  MessageClose,
  FieldError,
  ImagesGrid,
  ImageCard,
  ImageCardActions,
  ClosePageButton,
  HeaderBottom,
} from "./AdminStocPageStyles";

const CATEGORY_ORDER = [
  "cadouri_dulci",
  "cafea_ceai",
  "desert_pahar",
  "patiserie",
  "platouri_evenimente",
  "platouri_post",
  "prajituri_artizanale",
  "tasting_box",
  "torturi",
];

const CATEGORY_LABELS = {
  cadouri_dulci: "Cadouri Dulci",
  cafea_ceai: "Cafea & Ceai",
  desert_pahar: "Desert la Pahar",
  patiserie: "Patiserie",
  platouri_evenimente: "Platouri Evenimente",
  platouri_post: "Platouri de Post",
  prajituri_artizanale: "Prăjituri Artizanale",
  tasting_box: "Tasting Box",
  torturi: "Torturi",
};

const GET_ADMIN_PRODUCTS = gql`
  query GetAdminProducts {
    adminProducts {
      id
      categorie
      nume
      descriere
      descriere_lunga
      ingrediente
      alergeni
      calorii_kcal
      proteine_g
      carbohidrati_g
      grasimi_g
      sare_g
      zaharuri_g
      pret
      ordine
      imagine
      imagini {
        id
        image_url
        ordine
      }
      termen_ore
      gramaj
      stoc
      disponibil
      tip_pret
      gramaj_selectabil
      gramaj_min
      gramaj_max
      pas_gramaj
      reducere_procent
      reducere_tip
      pret_redus
      permite_mentiuni
      este_tort_personalizat
      afiseaza_pret_variabil
      afiseaza_per_buc
      created_at
    }
  }
`;

const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: ProductInput!) {
    createProduct(input: $input) {
      id
      categorie
      nume
      descriere
      descriere_lunga
      ingrediente
      alergeni
      calorii_kcal
      proteine_g
      carbohidrati_g
      grasimi_g
      sare_g
      zaharuri_g
      pret
      ordine
      imagine
      imagini {
        id
        image_url
        ordine
      }
      termen_ore
      gramaj
      stoc
      disponibil
      tip_pret
      gramaj_selectabil
      gramaj_min
      gramaj_max
      pas_gramaj
      reducere_procent
      reducere_tip
      pret_redus
      permite_mentiuni
      este_tort_personalizat
      afiseaza_pret_variabil
      afiseaza_per_buc
      created_at
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($id: ID!, $input: ProductInput!) {
    updateProduct(id: $id, input: $input) {
      id
      categorie
      nume
      descriere
      descriere_lunga
      ingrediente
      alergeni
      calorii_kcal
      proteine_g
      carbohidrati_g
      grasimi_g
      sare_g
      zaharuri_g
      pret
      ordine
      imagine
      imagini {
        id
        image_url
        ordine
      }
      termen_ore
      gramaj
      stoc
      disponibil
      tip_pret
      gramaj_selectabil
      gramaj_min
      gramaj_max
      pas_gramaj
      reducere_procent
      reducere_tip
      pret_redus
      permite_mentiuni
      este_tort_personalizat
      afiseaza_pret_variabil
      afiseaza_per_buc
      created_at
    }
  }
`;

const DELETE_PRODUCT = gql`
  mutation DeleteProduct($id: ID!) {
    deleteProduct(id: $id) {
      message
    }
  }
`;

const EMPTY_FORM = {
  categorie: "torturi",
  nume: "",
  descriere: "",
  descriere_lunga: "",
  ingrediente: "",
  alergeni: "",
  calorii_kcal: "",
  proteine_g: "",
  carbohidrati_g: "",
  grasimi_g: "",
  sare_g: "",
  zaharuri_g: "",
  pret: "",
  ordine: "",
  imagine: "",
  imagini: [],
  termen_ore: "24",
  gramaj: "",
  stoc: "0",
  disponibil: true,
  tip_pret: "fix",
  gramaj_selectabil: false,
  gramaj_min: "",
  gramaj_max: "",
  pas_gramaj: "",
  reducere_procent: "0",
  reducere_tip: "procent",
  pret_redus: "",
  permite_mentiuni: true,
  este_tort_personalizat: false,
  afiseaza_pret_variabil: false,
  afiseaza_per_buc: false,
};

function getStoredUser() {
  const possibleKeys = ["user", "authUser", "currentUser", "ahava_user"];

  for (const key of possibleKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed) return parsed;
    } catch {
      // ignore
    }
  }

  return null;
}

function toNullableFloat(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function toNullableInt(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function mapProductToForm(product) {
  if (!product) return EMPTY_FORM;

  const imagini = Array.isArray(product.imagini)
    ? product.imagini
        .slice()
        .sort((a, b) => Number(a.ordine ?? 0) - Number(b.ordine ?? 0))
        .map((img, index) => ({
          id: img.id ?? `${index}`,
          image_url: img.image_url ?? "",
          ordine: Number(img.ordine ?? index),
        }))
    : product.imagine
      ? [
          {
            id: "0",
            image_url: product.imagine,
            ordine: 0,
          },
        ]
      : [];

  return {
    categorie: product.categorie ?? "torturi",
    nume: product.nume ?? "",
    descriere: product.descriere ?? "",
    descriere_lunga: product.descriere_lunga ?? "",
    ingrediente: product.ingrediente ?? "",
    alergeni: product.alergeni ?? "",
    calorii_kcal: product.calorii_kcal ?? "",
    proteine_g: product.proteine_g ?? "",
    carbohidrati_g: product.carbohidrati_g ?? "",
    grasimi_g: product.grasimi_g ?? "",
    sare_g: product.sare_g ?? "",
    zaharuri_g: product.zaharuri_g ?? "",
    pret: product.pret ?? "",
    ordine: product.ordine ?? "",
    imagine: imagini[0]?.image_url ?? "",
    imagini,
    termen_ore: product.termen_ore ?? "24",
    gramaj: product.gramaj ?? "",
    stoc: product.stoc ?? "0",
    disponibil: product.disponibil ?? true,
    tip_pret: product.tip_pret ?? "fix",
    gramaj_selectabil: product.gramaj_selectabil ?? false,
    gramaj_min: product.gramaj_min ?? "",
    gramaj_max: product.gramaj_max ?? "",
    pas_gramaj: product.pas_gramaj ?? "",
    reducere_procent: product.reducere_procent ?? "0",
    reducere_tip: product.reducere_tip ?? "procent",
    pret_redus: product.pret_redus ?? "",
    permite_mentiuni: product.permite_mentiuni ?? true,
    este_tort_personalizat: product.este_tort_personalizat ?? false,
    afiseaza_pret_variabil: product.afiseaza_pret_variabil ?? false,
    afiseaza_per_buc: product.afiseaza_per_buc ?? false,
  };
}

function buildPayload(form, existingProduct = null) {
  const currentImages =
    Array.isArray(form.imagini) && form.imagini.length
      ? form.imagini
      : Array.isArray(existingProduct?.imagini) &&
          existingProduct.imagini.length
        ? existingProduct.imagini.map((img, index) => ({
            image_url: img.image_url || "",
            ordine: Number(img.ordine ?? index),
          }))
        : form.imagine
          ? [
              {
                image_url: String(form.imagine).trim(),
                ordine: 0,
              },
            ]
          : existingProduct?.imagine
            ? [
                {
                  image_url: String(existingProduct.imagine).trim(),
                  ordine: 0,
                },
              ]
            : [];

  const imagini = currentImages
    .filter((img) => String(img?.image_url || "").trim())
    .map((img, index) => ({
      image_url: String(img.image_url || "").trim(),
      ordine: index,
    }));

  return {
    categorie: form.categorie,
    nume: String(form.nume || "").trim(),
    descriere: String(form.descriere || "").trim() || null,
    descriere_lunga: String(form.descriere_lunga || "").trim() || null,
    ingrediente: String(form.ingrediente || "").trim() || null,
    alergeni: String(form.alergeni || "").trim() || null,
    calorii_kcal: toNullableFloat(form.calorii_kcal),
    proteine_g: toNullableFloat(form.proteine_g),
    carbohidrati_g: toNullableFloat(form.carbohidrati_g),
    grasimi_g: toNullableFloat(form.grasimi_g),
    sare_g: toNullableFloat(form.sare_g),
    zaharuri_g: toNullableFloat(form.zaharuri_g),
    pret: toNullableFloat(form.pret),
    ordine: toNullableInt(form.ordine) ?? 0,
    imagine:
      imagini[0]?.image_url ||
      String(form.imagine || "").trim() ||
      String(existingProduct?.imagine || "").trim() ||
      null,
    imagini,
    termen_ore: toNullableInt(form.termen_ore) ?? 0,
    gramaj: toNullableFloat(form.gramaj),
    stoc: toNullableInt(form.stoc) ?? 0,
    disponibil: !!form.disponibil,
    tip_pret: form.tip_pret || "fix",
    gramaj_selectabil: !!form.gramaj_selectabil,
    gramaj_min: toNullableFloat(form.gramaj_min),
    gramaj_max: toNullableFloat(form.gramaj_max),
    pas_gramaj: toNullableFloat(form.pas_gramaj),
    reducere_tip: form.reducere_tip || "procent",
    reducere_procent:
      form.reducere_tip === "procent"
        ? (toNullableFloat(form.reducere_procent) ?? 0)
        : 0,
    pret_redus:
      form.reducere_tip === "pret_nou"
        ? toNullableFloat(form.pret_redus)
        : null,
    permite_mentiuni: !!form.permite_mentiuni,
    este_tort_personalizat: !!form.este_tort_personalizat,
    afiseaza_pret_variabil: !!form.afiseaza_pret_variabil,
    afiseaza_per_buc: !!form.afiseaza_per_buc,
  };
}

function formatLei(value) {
  const n = Number(value || 0);

  return n.toLocaleString("ro-RO", {
    minimumFractionDigits: n % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  });
}

export default function AdminStocPage() {
  const navigate = useNavigate();
  const errorRef = useRef(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [checkedAuth, setCheckedAuth] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("toate");
  const [statusFilter, setStatusFilter] = useState("toate");
  const [isCreating, setIsCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const [imageUploadMessage, setImageUploadMessage] = useState("");

  const scrollToMessage = () => {
    setTimeout(() => {
      if (!errorRef.current) return;

      const y =
        errorRef.current.getBoundingClientRect().top + window.pageYOffset - 200;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }, 50);
  };

  const handleImageUpload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;

    try {
      setUploadingImage(true);

      const uploadedUrls = [];

      for (const file of list) {
        const fileExt = file.name.split(".").pop();
        const fileName = `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}.${fileExt}`;

        const filePath = fileName;

        const { error: uploadError } = await supabase.storage
          .from("produse_admin")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from("produse_admin")
          .getPublicUrl(filePath);

        if (!data?.publicUrl) {
          throw new Error("Nu am putut obține URL-ul imaginii.");
        }

        uploadedUrls.push(data.publicUrl);
      }

      setForm((prev) => {
        const existing = Array.isArray(prev.imagini) ? prev.imagini : [];

        const nextImages = [
          ...existing,
          ...uploadedUrls.map((url, index) => ({
            id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
            image_url: url,
            ordine: existing.length + index,
          })),
        ];

        return {
          ...prev,
          imagini: nextImages,
          imagine: nextImages[0]?.image_url || "",
        };
      });

      setImageUploadMessage(
        uploadedUrls.length === 1
          ? "Imagine adăugată. Nu uita să salvezi modificările."
          : `${uploadedUrls.length} imagini adăugate. Nu uita să salvezi modificările.`,
      );

      setTimeout(() => {
        setImageUploadMessage("");
      }, 5000);
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "A apărut o eroare la încărcarea imaginilor.",
      });
      scrollToMessage();
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setForm((prev) => {
      const nextImages = (prev.imagini || [])
        .filter((_, index) => index !== indexToRemove)
        .map((img, index) => ({
          ...img,
          ordine: index,
        }));

      return {
        ...prev,
        imagini: nextImages,
        imagine: nextImages[0]?.image_url || "",
      };
    });
  };

  const handleMakePrimaryImage = (indexToPromote) => {
    setForm((prev) => {
      const current = Array.isArray(prev.imagini) ? [...prev.imagini] : [];
      if (!current[indexToPromote]) return prev;

      const selected = current[indexToPromote];
      current.splice(indexToPromote, 1);

      const nextImages = [selected, ...current].map((img, index) => ({
        ...img,
        ordine: index,
      }));

      return {
        ...prev,
        imagini: nextImages,
        imagine: nextImages[0]?.image_url || "",
      };
    });
  };

  useEffect(() => {
    const user = getStoredUser();
    const role = user?.role;

    if (user && role === "admin") {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }

    setCheckedAuth(true);
  }, []);

  const { data, loading, error, refetch } = useQuery(GET_ADMIN_PRODUCTS, {
    skip: !checkedAuth || !isAdmin,
    fetchPolicy: "network-only",
  });

  const [createProduct, { loading: creating }] = useMutation(CREATE_PRODUCT);
  const [updateProduct, { loading: updating }] = useMutation(UPDATE_PRODUCT);
  const [deleteProduct, { loading: deleting }] = useMutation(DELETE_PRODUCT);

  const saving = creating || updating;

  const products = useMemo(() => {
    return data?.adminProducts ?? [];
  }, [data]);

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (categoryFilter !== "toate") {
      list = list.filter((p) => p.categorie === categoryFilter);
    }

    if (statusFilter === "active") {
      list = list.filter((p) => p.disponibil === true);
    }

    if (statusFilter === "inactive") {
      list = list.filter((p) => p.disponibil === false);
    }

    if (search.trim()) {
      const q = normalizeSearchText(search);

      list = list.filter((p) => {
        return (
          normalizeSearchText(p.nume).includes(q) ||
          normalizeSearchText(p.categorie).includes(q) ||
          normalizeSearchText(p.descriere).includes(q) ||
          normalizeSearchText(p.descriere_lunga).includes(q) ||
          normalizeSearchText(p.ingrediente).includes(q) ||
          normalizeSearchText(p.alergeni).includes(q)
        );
      });
    }

    return list.sort((a, b) => {
      const catA = CATEGORY_ORDER.indexOf(a.categorie);
      const catB = CATEGORY_ORDER.indexOf(b.categorie);

      if (catA !== catB) return catA - catB;

      const ordA = Number(a.ordine ?? 0);
      const ordB = Number(b.ordine ?? 0);
      if (ordA !== ordB) return ordA - ordB;

      return String(a.nume || "").localeCompare(String(b.nume || ""));
    });
  }, [products, categoryFilter, statusFilter, search]);

  const selectedProduct = useMemo(() => {
    return products.find((p) => String(p.id) === String(selectedId)) ?? null;
  }, [products, selectedId]);

  useEffect(() => {
    if (!selectedProduct || isCreating) return;
    setForm(mapProductToForm(selectedProduct));
    setFieldErrors({});
  }, [selectedProduct, isCreating]);

  const handleNewProduct = () => {
    setSelectedId(null);
    setIsCreating(true);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setMessage({ type: "", text: "" });
  };

  const handleSelectProduct = (product) => {
    setSelectedId(product.id);
    setIsCreating(false);
    setForm(mapProductToForm(product));
    setFieldErrors({});
    setMessage({ type: "", text: "" });
  };

  const handleCancel = () => {
    if (isCreating) {
      setForm(EMPTY_FORM);
      setIsCreating(false);
      setFieldErrors({});
      setMessage({ type: "", text: "" });
      return;
    }

    if (selectedProduct) {
      setForm(mapProductToForm(selectedProduct));
      setFieldErrors({});
      setMessage({ type: "", text: "" });
    }
  };

  const handleChange = (field, value) => {
    setMessage((prev) =>
      prev.type === "error" ? { type: "", text: "" } : prev,
    );

    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "disponibil" && value === false) {
        next.stoc = "0";
      }

      if (field === "tip_pret") {
        if (value === "fix") {
          next.afiseaza_pret_variabil = false;
          next.gramaj_selectabil = false;
          next.gramaj_min = "";
          next.gramaj_max = "";
          next.pas_gramaj = "";
        }

        if (value === "variabil") {
          next.afiseaza_pret_variabil = true;
          next.afiseaza_per_buc = false;
          next.gramaj_selectabil = true;

          if (!next.gramaj_min) next.gramaj_min = "1";
          if (!next.gramaj_max) next.gramaj_max = "5";
          if (!next.pas_gramaj) next.pas_gramaj = "0.5";
        }
      }

      if (field === "reducere_tip") {
        if (value === "procent") {
          next.pret_redus = "";
          if (!next.reducere_procent) next.reducere_procent = "0";
        }

        if (value === "pret_nou") {
          next.reducere_procent = "0";
        }
      }

      return next;
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!String(form.nume || "").trim()) {
      errors.nume = "Numele produsului este obligatoriu.";
    }

    if (!String(form.categorie || "").trim()) {
      errors.categorie = "Categoria este obligatorie.";
    }

    const pretNumber = Number(form.pret);

    if (
      form.pret === "" ||
      form.pret === null ||
      pretNumber % 0.5 !== 0 ||
      pretNumber <= 0
    ) {
      errors.pret =
        form.tip_pret === "variabil"
          ? "Prețul pe kg trebuie să fie mai mare ca 0 și poate avea valori de tip 7,5."
          : "Prețul trebuie să fie mai mare ca 0 și poate avea valori de tip 7,5.";
    }

    if (form.tip_pret === "variabil") {
      if (form.gramaj_min === "" || Number(form.gramaj_min) <= 0) {
        errors.gramaj_min = "Gramajul minim este obligatoriu.";
      }

      if (form.gramaj_max === "" || Number(form.gramaj_max) <= 0) {
        errors.gramaj_max = "Gramajul maxim este obligatoriu.";
      }

      if (form.pas_gramaj === "" || Number(form.pas_gramaj) <= 0) {
        errors.pas_gramaj = "Pasul de gramaj este obligatoriu.";
      }
    }

    if (form.reducere_tip === "procent") {
      const reducere = Number(form.reducere_procent || 0);
      if (reducere < 0 || reducere > 100) {
        errors.reducere_procent = "Reducerea trebuie să fie între 0 și 100.";
      }
    }

    if (form.reducere_tip === "pret_nou") {
      const pret = Number(form.pret || 0);
      const pretRedus = Number(form.pret_redus || 0);

      if (!pretRedus || pretRedus <= 0) {
        errors.pret_redus = "Prețul nou trebuie să fie mai mare ca 0.";
      } else if (pret > 0 && pretRedus >= pret) {
        errors.pret_redus =
          "Prețul nou trebuie să fie mai mic decât prețul inițial.";
      }
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setMessage({
        type: "error",
        text: "Te rog completează câmpurile obligatorii.",
      });
      scrollToMessage();
      return false;
    }

    setMessage({ type: "", text: "" });
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const input = buildPayload(form, selectedProduct);

    try {
      if (isCreating) {
        const res = await createProduct({
          variables: { input },
        });

        const createdId = res?.data?.createProduct?.id;
        await refetch();

        if (createdId) {
          setSelectedId(createdId);
          setIsCreating(false);
        }

        setFieldErrors({});
        setMessage({
          type: "success",
          text: "Produsul a fost adăugat cu succes.",
        });
        scrollToMessage();
        return;
      }

      if (!selectedId) {
        setMessage({
          type: "error",
          text: "Nu există produs selectat pentru editare.",
        });
        scrollToMessage();
        return;
      }

      await updateProduct({
        variables: {
          id: selectedId,
          input,
        },
      });

      await refetch();

      setFieldErrors({});
      setMessage({
        type: "success",
        text: "Produsul a fost actualizat cu succes.",
      });
      scrollToMessage();
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "A apărut o eroare la salvare.",
      });
      scrollToMessage();
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;

    const ok = window.confirm(
      "Sigur vrei să ștergi acest produs? Acțiunea nu se poate anula.",
    );
    if (!ok) return;

    try {
      await deleteProduct({
        variables: { id: selectedId },
      });

      await refetch();
      setSelectedId(null);
      setIsCreating(false);
      setForm(EMPTY_FORM);
      setFieldErrors({});

      setMessage({
        type: "success",
        text: "Produsul a fost șters cu succes.",
      });
      scrollToMessage();
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Nu am putut șterge produsul.",
      });
      scrollToMessage();
    }
  };

  const handleToggleAvailability = async (product) => {
    try {
      const nextDisponibil = !product.disponibil;

      await updateProduct({
        variables: {
          id: product.id,
          input: {
            categorie: product.categorie,
            nume: product.nume,
            descriere: product.descriere,
            descriere_lunga: product.descriere_lunga,
            ingrediente: product.ingrediente,
            alergeni: product.alergeni,
            calorii_kcal: product.calorii_kcal,
            proteine_g: product.proteine_g,
            carbohidrati_g: product.carbohidrati_g,
            grasimi_g: product.grasimi_g,
            sare_g: product.sare_g,
            zaharuri_g: product.zaharuri_g,
            pret: product.pret,
            ordine: product.ordine,
            imagine: product.imagine,
            imagini: (product.imagini || []).map((img, index) => ({
              image_url: img.image_url,
              ordine: Number(img.ordine ?? index),
            })),
            termen_ore: product.termen_ore,
            gramaj: product.gramaj,
            stoc: nextDisponibil ? product.stoc : 0,
            disponibil: nextDisponibil,
            tip_pret: product.tip_pret,
            gramaj_selectabil: product.gramaj_selectabil,
            gramaj_min: product.gramaj_min,
            gramaj_max: product.gramaj_max,
            pas_gramaj: product.pas_gramaj,
            reducere_procent: product.reducere_procent,
            reducere_tip: product.reducere_tip,
            pret_redus: product.pret_redus,
            permite_mentiuni: product.permite_mentiuni,
            este_tort_personalizat: product.este_tort_personalizat,
            afiseaza_pret_variabil: product.afiseaza_pret_variabil,
            afiseaza_per_buc: product.afiseaza_per_buc,
          },
        },
      });

      await refetch();

      if (String(selectedId) === String(product.id) && !nextDisponibil) {
        setForm((prev) => ({
          ...prev,
          disponibil: false,
          stoc: "0",
        }));
      }

      if (String(selectedId) === String(product.id) && nextDisponibil) {
        setForm((prev) => ({
          ...prev,
          disponibil: true,
        }));
      }

      setMessage({
        type: "success",
        text: nextDisponibil
          ? "Produsul a fost activat."
          : "Produsul a fost dezactivat și stocul a fost setat la 0.",
      });
      scrollToMessage();
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Nu am putut schimba disponibilitatea.",
      });
      scrollToMessage();
    }
  };

  const totalVisible = filteredProducts.length;
  const totalProducts = products.length;

  if (!checkedAuth) {
    return (
      <PageWrap>
        <InfoBox>Se verifică accesul...</InfoBox>
      </PageWrap>
    );
  }

  if (!isAdmin) {
    return (
      <ForbiddenWrap>
        <h2>Acces restricționat</h2>
        <p>Această pagină este disponibilă doar pentru admin.</p>
        <PrimaryButton type="button" onClick={() => navigate("/")}>
          Înapoi la site
        </PrimaryButton>
      </ForbiddenWrap>
    );
  }

  return (
    <PageWrap>
      <ClosePageButton type="button" onClick={() => navigate("/")}>
        ×
      </ClosePageButton>

      <Header>
        <HeaderCenter>
          <Title>Administrare produse</Title>

          <Ornament>
            <OrnLine />
            <OrnDiamond />
          </Ornament>

          <Subtitle>Adaugă, modifică și gestionează rapid produsele.</Subtitle>
        </HeaderCenter>

        <HeaderBottom>
          <HeaderLeft>
            <Badge>{totalProducts} produse</Badge>
          </HeaderLeft>

          <HeaderRight>
            <PrimaryButton type="button" onClick={handleNewProduct}>
              + Produs nou
            </PrimaryButton>
          </HeaderRight>
        </HeaderBottom>
      </Header>

      <Layout>
        <Sidebar>
          <Panel>
            <PanelTitle>Produse</PanelTitle>

            <SearchInput
              type="text"
              placeholder="Caută după nume, categorie sau descriere..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <FilterRow>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="toate">Toate categoriile</option>
                {CATEGORY_ORDER.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABELS[cat] || cat}
                  </option>
                ))}
              </Select>

              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="toate">Toate</option>
                <option value="active">Disponibile</option>
                <option value="inactive">Indisponibile</option>
              </Select>
            </FilterRow>

            <SmallText>
              Se afișează {totalVisible} din {totalProducts} produse.
            </SmallText>

            <Divider />

            {loading ? (
              <InfoBox>Se încarcă produsele...</InfoBox>
            ) : error ? (
              <InfoBox>Eroare la încărcare. Verifică backend-ul.</InfoBox>
            ) : filteredProducts.length === 0 ? (
              <EmptyState>Nu există produse pentru filtrele alese.</EmptyState>
            ) : (
              <ProductList>
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    $active={
                      String(selectedId) === String(product.id) && !isCreating
                    }
                    onClick={() => handleSelectProduct(product)}
                  >
                    <ProductTop>
                      <div>
                        <ProductName>{product.nume}</ProductName>
                        <ProductMeta>
                          {CATEGORY_LABELS[product.categorie] ||
                            product.categorie}
                        </ProductMeta>
                      </div>

                      <ProductStatus $ok={product.disponibil}>
                        {product.disponibil ? "Disponibil" : "Indisponibil"}
                      </ProductStatus>
                    </ProductTop>

                    <ProductMeta>
                      Stoc: {product.stoc ?? 0} • Ordine: {product.ordine ?? 0}
                    </ProductMeta>

                    <ProductPrice>
                      {product.pret != null
                        ? product.tip_pret === "variabil"
                          ? `${product.pret} lei / kg`
                          : product.afiseaza_per_buc
                            ? `${product.pret} lei / buc`
                            : `${formatLei(product.pret)} lei`
                        : "Preț necompletat"}
                    </ProductPrice>

                    <ProductActions>
                      <SecondaryButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectProduct(product);
                        }}
                      >
                        Editează
                      </SecondaryButton>

                      <SecondaryButton
                        type="button"
                        disabled={saving}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleAvailability(product);
                        }}
                      >
                        {product.disponibil ? "Dezactivează" : "Activează"}
                      </SecondaryButton>
                    </ProductActions>
                  </ProductCard>
                ))}
              </ProductList>
            )}
          </Panel>
        </Sidebar>

        <Main>
          <Panel>
            <PanelTitle>
              {isCreating
                ? "Adaugă produs nou"
                : selectedProduct
                  ? `Editezi: ${selectedProduct.nume}`
                  : "Selectează un produs"}
            </PanelTitle>

            {message.text ? (
              <MessageBox ref={errorRef} $type={message.type}>
                <MessageIcon $type={message.type}>
                  {message.type === "error" ? "!" : "✓"}
                </MessageIcon>

                <MessageContent>
                  <MessageTitle>
                    {message.type === "error" ? "Atenție" : "Succes"}
                  </MessageTitle>
                  <MessageText>{message.text}</MessageText>
                </MessageContent>

                <MessageClose
                  type="button"
                  onClick={() => setMessage({ type: "", text: "" })}
                >
                  ×
                </MessageClose>
              </MessageBox>
            ) : null}

            {!isCreating && !selectedProduct ? (
              <EmptyState>
                Alege un produs din listă sau apasă pe „Produs nou”.
              </EmptyState>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <Section>
                  <SectionTitle>Informații principale</SectionTitle>
                  <SectionHint>
                    Aici completezi ce vede clientul prima dată: produsul,
                    categoria, descrierea și imaginea.
                  </SectionHint>

                  <StatusBox $ok={!!form.disponibil}>
                    {form.disponibil
                      ? "Produsul este vizibil și poate fi comandat."
                      : "Produsul este salvat, dar momentan nu poate fi comandat."}
                  </StatusBox>

                  <FormGrid>
                    <Field>
                      <Label style={{ visibility: "hidden" }}>
                        Disponibilitate
                      </Label>
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          checked={!!form.disponibil}
                          onChange={(e) =>
                            handleChange("disponibil", e.target.checked)
                          }
                        />
                        <CheckboxLabel>Disponibil pentru comandă</CheckboxLabel>
                      </CheckboxRow>
                    </Field>

                    <Field>
                      <Label>Categorie</Label>
                      <Select
                        $error={!!fieldErrors.categorie}
                        value={form.categorie}
                        onChange={(e) =>
                          handleChange("categorie", e.target.value)
                        }
                      >
                        {CATEGORY_ORDER.map((cat) => (
                          <option key={cat} value={cat}>
                            {CATEGORY_LABELS[cat] || cat}
                          </option>
                        ))}
                      </Select>
                      {fieldErrors.categorie ? (
                        <FieldError>{fieldErrors.categorie}</FieldError>
                      ) : null}
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <Label>Nume produs</Label>
                      <Input
                        $error={!!fieldErrors.nume}
                        type="text"
                        value={form.nume}
                        onChange={(e) => handleChange("nume", e.target.value)}
                        placeholder="Ex: Tort Red Velvet"
                      />
                      {fieldErrors.nume ? (
                        <FieldError>{fieldErrors.nume}</FieldError>
                      ) : null}
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <Label>Descriere</Label>
                      <TextArea
                        value={form.descriere}
                        onChange={(e) =>
                          handleChange("descriere", e.target.value)
                        }
                        placeholder="Descriere scurtă a produsului"
                      />
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <Label>Descriere lungă</Label>
                      <TextArea
                        value={form.descriere_lunga}
                        onChange={(e) =>
                          handleChange("descriere_lunga", e.target.value)
                        }
                        placeholder="Descriere detaliată care va apărea în tabul «Detalii»"
                      />
                      <HelperText>
                        Acest text va fi afișat în modalul produsului, în tabul
                        „Detalii”.
                      </HelperText>
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <Label>Ingrediente</Label>
                      <TextArea
                        value={form.ingrediente}
                        onChange={(e) =>
                          handleChange("ingrediente", e.target.value)
                        }
                        placeholder="Ex: făină, ouă, unt, zahăr, ciocolată..."
                      />
                      <HelperText>
                        Acest text va fi afișat în tabul „Ingrediente”.
                      </HelperText>
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <Label>Alergeni</Label>
                      <TextArea
                        value={form.alergeni}
                        onChange={(e) =>
                          handleChange("alergeni", e.target.value)
                        }
                        placeholder="Ex: gluten, lapte, ouă. Poate conține urme de nuci."
                      />
                      <HelperText>
                        Acest text va fi afișat în tabul „Alergeni”.
                      </HelperText>
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <Label>Imagini produs</Label>
                      <HiddenFileInput
                        id="product-image-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files?.length) {
                            handleImageUpload(files);
                          }
                          e.target.value = "";
                        }}
                      />

                      <UploadButton htmlFor="product-image-upload">
                        {uploadingImage ? "Se încarcă..." : "Încarcă imagini"}
                      </UploadButton>

                      <HelperText>
                        Poți încărca una sau mai multe imagini. Prima va fi
                        folosită ca imagine principală în meniu.
                      </HelperText>

                      {form.imagini?.length ? (
                        <>
                          <HelperText>
                            Prima imagine din listă va fi afișată pe cardul
                            produsului în meniu.
                          </HelperText>

                          <ImagesGrid>
                            {form.imagini.map((img, index) => (
                              <ImageCard
                                key={img.id || `${img.image_url}-${index}`}
                              >
                                <ImagePreview
                                  src={img.image_url}
                                  alt={`Preview produs ${index + 1}`}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />

                                <ImageCardActions>
                                  <SecondaryButton
                                    type="button"
                                    onClick={() =>
                                      handleMakePrimaryImage(index)
                                    }
                                  >
                                    {index === 0
                                      ? "Principală"
                                      : "Fă principală"}
                                  </SecondaryButton>

                                  <DangerButton
                                    type="button"
                                    onClick={() => handleRemoveImage(index)}
                                  >
                                    Șterge
                                  </DangerButton>
                                </ImageCardActions>
                              </ImageCard>
                            ))}
                          </ImagesGrid>

                          {imageUploadMessage ? (
                            <HelperText
                              style={{ color: "#44a33b", fontWeight: 700 }}
                            >
                              {imageUploadMessage}
                            </HelperText>
                          ) : null}
                        </>
                      ) : null}
                    </Field>
                  </FormGrid>
                </Section>

                <Divider />

                <Section>
                  <SectionTitle>Valori nutriționale</SectionTitle>
                  <SectionHint>
                    Aceste valori vor fi afișate în tabul „Nutriție” din
                    detaliile produsului.
                  </SectionHint>

                  <FormGrid>
                    <Field>
                      <Label>Calorii (kcal)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.calorii_kcal}
                        onChange={(e) =>
                          handleChange("calorii_kcal", e.target.value)
                        }
                        placeholder="Ex: 320.20"
                      />
                    </Field>

                    <Field>
                      <Label>Proteine (g)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.proteine_g}
                        onChange={(e) =>
                          handleChange("proteine_g", e.target.value)
                        }
                        placeholder="Ex: 5.40"
                      />
                    </Field>

                    <Field>
                      <Label>Carbohidrați (g)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.carbohidrati_g}
                        onChange={(e) =>
                          handleChange("carbohidrati_g", e.target.value)
                        }
                        placeholder="Ex: 42.80"
                      />
                    </Field>

                    <Field>
                      <Label>Grăsimi (g)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.grasimi_g}
                        onChange={(e) =>
                          handleChange("grasimi_g", e.target.value)
                        }
                        placeholder="Ex: 19.90"
                      />
                    </Field>

                    <Field>
                      <Label>Sare (g)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.sare_g}
                        onChange={(e) => handleChange("sare_g", e.target.value)}
                        placeholder="Ex: 0.18"
                      />
                    </Field>

                    <Field>
                      <Label>Zaharuri (g)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.zaharuri_g}
                        onChange={(e) =>
                          handleChange("zaharuri_g", e.target.value)
                        }
                        placeholder="Ex: 30.00"
                      />
                    </Field>
                  </FormGrid>
                </Section>

                <Divider />

                <Section>
                  <SectionTitle>Preț și configurare</SectionTitle>
                  <SectionHint>
                    Stabilești dacă produsul are preț fix sau preț în funcție de
                    gramaj.
                  </SectionHint>

                  <FormGrid>
                    <Field>
                      <Label>Tip preț</Label>
                      <Select
                        value={form.tip_pret}
                        onChange={(e) =>
                          handleChange("tip_pret", e.target.value)
                        }
                      >
                        <option value="fix">Fix</option>
                        <option value="variabil">Variabil</option>
                      </Select>
                      <HelperText>
                        Fix = un singur preț. Variabil = preț calculat după
                        gramaj.
                      </HelperText>
                    </Field>

                    <Field>
                      <Label>
                        {form.tip_pret === "variabil" ? "Preț / kg" : "Preț"}
                      </Label>
                      <Input
                        $error={!!fieldErrors.pret}
                        type="number"
                        step="0.5"
                        min="1"
                        value={form.pret}
                        onChange={(e) => handleChange("pret", e.target.value)}
                        placeholder="Ex: 120"
                      />
                      {fieldErrors.pret ? (
                        <FieldError>{fieldErrors.pret}</FieldError>
                      ) : null}
                    </Field>
                    {form.tip_pret === "variabil" ? (
                      <Field style={{ gridColumn: "1 / -1" }}>
                        <CheckboxRow>
                          <input type="checkbox" checked readOnly />
                          <CheckboxLabel>
                            Clientul poate alege gramajul
                          </CheckboxLabel>
                        </CheckboxRow>
                        <HelperText>
                          Pentru produsele cu preț variabil, gramajul este
                          selectabil automat.
                        </HelperText>
                      </Field>
                    ) : null}

                    {form.tip_pret === "variabil" ? (
                      <Field style={{ gridColumn: "1 / -1" }}>
                        <CheckboxRow>
                          <input
                            type="checkbox"
                            checked={!!form.afiseaza_pret_variabil}
                            onChange={(e) =>
                              handleChange(
                                "afiseaza_pret_variabil",
                                e.target.checked,
                              )
                            }
                          />
                          <CheckboxLabel>
                            Afișează „Preț variabil”
                          </CheckboxLabel>
                        </CheckboxRow>
                        <HelperText>
                          Dacă este bifat, în site va apărea „Preț variabil”.
                          Dacă nu este bifat, se va afișa prețul normal per kg.
                        </HelperText>
                      </Field>
                    ) : null}
                    {form.tip_pret === "fix" && (
                      <>
                        <Field>
                          <Label>Gramaj afișat (opțional)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={form.gramaj}
                            onChange={(e) =>
                              handleChange("gramaj", e.target.value)
                            }
                            placeholder="Ex: 170"
                          />
                          <HelperText>
                            Va fi afișat în site ca: Cantitate (+/- 10%): X g
                          </HelperText>
                        </Field>

                        <Field>
                          <Label style={{ visibility: "hidden" }}>
                            Afișare per bucată
                          </Label>
                          <CheckboxRow>
                            <input
                              type="checkbox"
                              checked={!!form.afiseaza_per_buc}
                              onChange={(e) =>
                                handleChange(
                                  "afiseaza_per_buc",
                                  e.target.checked,
                                )
                              }
                            />
                            <CheckboxLabel>
                              Afișează prețul cu „/ buc”
                            </CheckboxLabel>
                          </CheckboxRow>
                          <HelperText>
                            Dacă este bifat, în site va apărea de forma „15 lei
                            / buc”.
                          </HelperText>
                        </Field>
                      </>
                    )}

                    {form.tip_pret === "variabil" && form.gramaj_selectabil && (
                      <>
                        <Field>
                          <Label>Gramaj minim</Label>
                          <Input
                            $error={!!fieldErrors.gramaj_min}
                            type="number"
                            step="0.01"
                            value={form.gramaj_min}
                            onChange={(e) =>
                              handleChange("gramaj_min", e.target.value)
                            }
                            placeholder="Ex: 1"
                          />
                          {fieldErrors.gramaj_min ? (
                            <FieldError>{fieldErrors.gramaj_min}</FieldError>
                          ) : null}
                        </Field>

                        <Field>
                          <Label>Gramaj maxim</Label>
                          <Input
                            $error={!!fieldErrors.gramaj_max}
                            type="number"
                            step="0.01"
                            value={form.gramaj_max}
                            onChange={(e) =>
                              handleChange("gramaj_max", e.target.value)
                            }
                            placeholder="Ex: 5"
                          />
                          {fieldErrors.gramaj_max ? (
                            <FieldError>{fieldErrors.gramaj_max}</FieldError>
                          ) : null}
                        </Field>

                        <Field>
                          <Label>Pas gramaj</Label>
                          <Input
                            $error={!!fieldErrors.pas_gramaj}
                            type="number"
                            step="0.01"
                            value={form.pas_gramaj}
                            onChange={(e) =>
                              handleChange("pas_gramaj", e.target.value)
                            }
                            placeholder="Ex: 0.5"
                          />
                          {fieldErrors.pas_gramaj ? (
                            <FieldError>{fieldErrors.pas_gramaj}</FieldError>
                          ) : null}
                        </Field>
                      </>
                    )}
                  </FormGrid>
                </Section>

                <Divider />

                <Section>
                  <SectionTitle>Stoc și afișare</SectionTitle>
                  <SectionHint>
                    Controlezi câte produse sunt disponibile și ordinea în
                    pagină.
                  </SectionHint>

                  <FormGrid>
                    <Field>
                      <Label>{form.disponibil ? "Stoc" : "Stoc blocat"}</Label>
                      <Input
                        type="number"
                        value={form.stoc}
                        disabled={!form.disponibil}
                        onChange={(e) => handleChange("stoc", e.target.value)}
                        placeholder="Ex: 10"
                      />
                      {!form.disponibil ? (
                        <FieldError style={{ color: "#8a7e77" }}>
                          Câmp blocat cât timp produsul este indisponibil.
                        </FieldError>
                      ) : null}
                      <HelperText>
                        {form.disponibil
                          ? "Introdu cantitatea disponibilă în stoc."
                          : "Produsul este indisponibil, stocul a fost setat automat la 0 și câmpul este blocat."}
                      </HelperText>
                    </Field>

                    <Field>
                      <Label>Ordine afișare</Label>
                      <Input
                        type="number"
                        value={form.ordine}
                        onChange={(e) => handleChange("ordine", e.target.value)}
                        placeholder="Ex: 1"
                      />
                      <HelperText>
                        Număr mai mic = apare mai sus în categorie.
                      </HelperText>
                    </Field>

                    <Field>
                      <Label>Termen minim (ore)</Label>
                      <Input
                        type="number"
                        value={form.termen_ore}
                        onChange={(e) =>
                          handleChange("termen_ore", e.target.value)
                        }
                        placeholder="Ex: 24"
                      />
                      <HelperText>
                        În câte ore minim poate fi pregătit produsul.
                      </HelperText>
                    </Field>
                  </FormGrid>
                </Section>

                <Divider />

                <Section>
                  <SectionTitle>Reducere</SectionTitle>
                  <SectionHint>
                    Stabilește reducerea, fie prin procent sau printr-un preț
                    nou după reducere.
                  </SectionHint>

                  <FormGrid>
                    <Field>
                      <Label>Tip reducere</Label>
                      <Select
                        value={form.reducere_tip}
                        onChange={(e) =>
                          handleChange("reducere_tip", e.target.value)
                        }
                      >
                        <option value="procent">Reducere procentuală</option>
                        <option value="pret_nou">Preț nou</option>
                      </Select>
                    </Field>

                    {form.reducere_tip === "procent" ? (
                      <Field>
                        <Label>Reducere (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.reducere_procent}
                          onChange={(e) =>
                            handleChange("reducere_procent", e.target.value)
                          }
                          placeholder="Ex: 10"
                        />
                        <HelperText>
                          Lasă 0 dacă produsul nu are reducere.
                        </HelperText>
                        {fieldErrors.reducere_procent ? (
                          <FieldError>
                            {fieldErrors.reducere_procent}
                          </FieldError>
                        ) : null}
                      </Field>
                    ) : (
                      <Field>
                        <Label>Preț nou</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="1"
                          value={form.pret_redus}
                          onChange={(e) =>
                            handleChange("pret_redus", e.target.value)
                          }
                          placeholder="Ex: 89"
                        />
                        <HelperText>
                          Prețul final care va fi afișat clientului.
                        </HelperText>
                        {fieldErrors.pret_redus ? (
                          <FieldError>{fieldErrors.pret_redus}</FieldError>
                        ) : null}
                      </Field>
                    )}
                  </FormGrid>
                </Section>

                <Divider />

                <Section>
                  <SectionTitle>Opțiuni produs</SectionTitle>
                  <SectionHint>
                    Setări speciale pentru produse personalizabile sau produse
                    cu afișaj diferit în magazin.
                  </SectionHint>

                  <ToggleGrid>
                    <Field>
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          checked={!!form.permite_mentiuni}
                          onChange={(e) =>
                            handleChange("permite_mentiuni", e.target.checked)
                          }
                        />
                        <CheckboxLabel>Permite mențiuni</CheckboxLabel>
                      </CheckboxRow>
                      <HelperText>
                        Clientul poate scrie observații la comandă.
                      </HelperText>
                    </Field>
                  </ToggleGrid>
                </Section>

                <Divider />

                <InlineRow>
                  <PrimaryButton type="submit" disabled={saving}>
                    {saving
                      ? "Se salvează..."
                      : isCreating
                        ? "Adaugă produs"
                        : "Salvează modificările"}
                  </PrimaryButton>

                  <SecondaryButton type="button" onClick={handleCancel}>
                    Renunță
                  </SecondaryButton>

                  {!isCreating && selectedId ? (
                    <DangerButton
                      type="button"
                      disabled={deleting}
                      onClick={handleDelete}
                    >
                      {deleting ? "Se șterge..." : "Șterge produsul"}
                    </DangerButton>
                  ) : null}
                </InlineRow>
              </form>
            )}
          </Panel>
        </Main>
      </Layout>
    </PageWrap>
  );
}
